import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, Overline } from "../Reveal";
import { BRAND } from "../../data/content";

const CHAPTERS = [
  { no: "01", title: "The Studio", text: "GK Brandify is a luxury creative agency helping businesses create world-class digital experiences through premium branding, websites and technology." },
  { no: "02", title: "The Philosophy", text: "We believe restraint is luxury. Every mark, every motion and every pixel is crafted with intention — never decoration, always meaning." },
  { no: "03", title: "The Promise", text: "We partner with ambitious brands and treat their vision as our own, delivering work that endures and elevates for years to come." },
];

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-40, 60]);

  return (
    <section id="about" ref={ref} data-testid="about-section" className="relative py-16 md:py-24 overflow-hidden">
      <div className="ambient-emerald" style={{ width: 500, height: 500, top: "10%", right: "-12%", opacity: 0.3 }} />

      <div className="relative z-10 mx-auto max-w-6xl px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
        {/* Left — image */}
        <div className="lg:col-span-5 lg:sticky lg:top-32">
          <Reveal className="relative">
            <div className="relative rounded-3xl overflow-hidden ring-1 ring-white/10 bg-gk-black">
              <motion.img
                style={{ y }}
                src="/founder.png"
                alt={BRAND.founder}
                loading="lazy"
                className="w-full h-[400px] object-cover object-top scale-105 grayscale contrast-[1.15] brightness-[0.92]"
              />
              {/* Emerald duotone tint (colorises the grayscale photo) */}
              <div className="absolute inset-0 bg-gk-emerald mix-blend-color opacity-70" />
              {/* Depth: darken toward black so the bright studio backdrop melts into the theme */}
              <div className="absolute inset-0 bg-gradient-to-b from-gk-emerald/30 via-transparent to-gk-black/70 mix-blend-multiply" />
              {/* Vignette to erase the white edges */}
              <div className="absolute inset-0" style={{ background: "radial-gradient(120% 90% at 50% 30%, transparent 40%, rgba(10,10,10,0.9) 100%)" }} />
              {/* Gold light accent */}
              <div className="absolute inset-0 mix-blend-screen" style={{ background: "radial-gradient(circle at 78% 12%, rgba(212,175,55,0.28), transparent 45%)" }} />
              {/* Bottom fade for the name card */}
              <div className="absolute inset-0 bg-gradient-to-t from-gk-black via-gk-black/30 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 glass rounded-2xl p-5">
                <p className="font-cinzel text-base text-gold-metallic">{BRAND.founder}</p>
                <p className="font-inter text-xs tracking-[0.25em] uppercase text-white/50 mt-1">Founder & Creative Director</p>
              </div>
            </div>
            {/* gold frame accent */}
            <div className="absolute -top-4 -left-4 w-24 h-24 border-t border-l border-gk-gold/50 rounded-tl-3xl" />
            <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b border-r border-gk-gold/50 rounded-br-3xl" />
          </Reveal>
        </div>

        {/* Right — manifesto */}
        <div className="lg:col-span-7">
          <Reveal>
            <Overline>Who We Are</Overline>
            <h2 className="font-cinzel text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mt-4 leading-tight">
              A studio obsessed with <span className="text-gold-metallic">the exceptional.</span>
            </h2>
          </Reveal>

          <div className="mt-8 space-y-6">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.no} delay={i * 0.1} className="flex gap-5 group">
                <div className="shrink-0">
                  <span className="font-cinzel text-xl text-gk-gold/50 group-hover:text-gk-gold transition-colors duration-500">{c.no}</span>
                  <div className="w-px h-full bg-white/10 mx-auto mt-2" />
                </div>
                <div>
                  <h3 className="font-inter text-xs tracking-[0.25em] uppercase text-gk-champagne mb-2">{c.title}</h3>
                  <p className="font-poppins text-white/60 leading-relaxed text-sm max-w-xl">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
